import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useHealth } from '@/ui/hooks/useHealth'
import { BACKEND_ENABLED } from '@/infrastructure/config/featureFlags'

const STATUS_COLOR: Record<string, string> = {
  ok: 'var(--color-ok)',
  degraded: 'var(--color-warn)',
  down: 'var(--color-down)',
  loading: 'var(--color-muted)',
}

/**
 * Footer health pill — small color dot + status word, links to
 * /status. Polls /api/health every 30s via useHealth.
 *
 * The pill is purely informational; the offline review surface keeps
 * working even when the dot is red.
 */
export function HealthPill() {
  const { t } = useTranslation()
  // Backend disabled: hide the pill entirely. The footer falls back to
  // Wipe + Privacy links. The hook below is still always called to
  // honor React's rules of hooks, but with BACKEND_ENABLED=false the
  // hook itself never issues a request (see useHealth).
  const state = useHealth()
  if (!BACKEND_ENABLED) return null
  const key = state.status === 'loading' ? 'loading' : state.status
  const color = STATUS_COLOR[key] ?? 'var(--color-muted)'

  return (
    <Link
      to="/status"
      className="inline-flex items-center gap-2 no-underline"
      style={{ color: 'var(--color-muted)' }}
      title={t(`status.pillTitle.${key}`)}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          display: 'inline-block',
        }}
      />
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.04em' }}>
        {t(`status.pill.${key}`)}
      </span>
    </Link>
  )
}
